import React, { useState, useEffect, useRef } from "react";
import "./index.scss";
import Dropdown from "../../static/Dropdown/Dropdown";
import { dropdownItem } from "../../static/Dropdown/types";
import Table from "./Table";
import Results from "./Results";
import { getCalendarFilters } from "../../services/tournaments";
import { getResultsRoundUpFilters } from "../../services/resultsroundup";
import { formatDate, useOutsideAlerter, useDebounce } from "../../helpers";
import { tournamentCalendar } from "../../helpers/errors";
import perPage from "./pagerOptions";
import {
  WhatsOnTableProps,
  CourtSurface,
  CourtDoorness,
  dropdownItemSelected,
  HostDetails,
  ALL
} from "./types";
import moment from "moment";
import { monthLabelsFull } from "../../dynamic/DatePicker/helpers";
import { Dates, AdvancedFilters } from "../../dynamic/WhatsOnHero/types";
import WhatsOnStateToggleSection from "./WhatsOnStateToggle";
import DatePicker from "../../dynamic/DatePicker/DatePicker";
import _ from "lodash";
import { tourOptions } from "../RankingHero/mockData";
import MultiSelectDropdown from "./MultiSelectDropdown";
import { useLazyLoad } from "../../helpers/hooks";

interface WhatsOnAdvancedFiltersProps {
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  setDates: (dates: Dates) => void;
  advancedFilters: AdvancedFilters;
  setAdvancedFilters: (filters: AdvancedFilters) => void;
  externalMonthAndYearState?: {
    viewedMonth: number;
    setViewedMonth: (month: number) => void;
    viewedYear: number;
    setViewedYear: (year: number) => void;
  };
}

export const WhatsOnAdvancedFilters = (props: WhatsOnAdvancedFiltersProps) => {
  const {
    startDate,
    endDate,
    setDates,
    advancedFilters,
    setAdvancedFilters,
    externalMonthAndYearState
  } = props;
  const [showSurfaceFilters, setShowSurfaceFilters] = useState(false);

  const changeCourtSufaceState = (
    surfaceCode: string,
    active: boolean,
    currentCourtSurfaces: CourtSurface[]
  ) => {
    const nextCourtSurfaces: CourtSurface[] = currentCourtSurfaces.map(
      surface => {
        if (surface.code === surfaceCode) {
          return {
            name: surface.name,
            code: surface.code,
            active
          };
        } else {
          return surface;
        }
      }
    );
    return nextCourtSurfaces;
  };

  const changeCourtDoornessState = (
    surfaceCode: string,
    active: boolean,
    currentCourtDoorness: CourtDoorness[]
  ) => {
    const nextCourtDoorness: CourtDoorness[] = currentCourtDoorness.map(
      door => {
        if (door.code === surfaceCode) {
          return {
            code: door.code,
            active
          };
        } else {
          return door;
        }
      }
    );
    return nextCourtDoorness;
  };

  const changeCategory = (
    category: dropdownItemSelected,
    currentCategories: dropdownItemSelected[]
  ) => {
    const nextCategories: dropdownItemSelected[] = currentCategories.map(
      cat => {
        if (cat.value !== category.value) {
          return {
            ...cat,
            selected: cat.selected
          };
        } else {
          return {
            ...cat,
            selected: !cat.selected
          };
        }
      }
    );

    let selected = nextCategories.filter(cat => cat.selected);
    const flag = selected.some(cat => cat.value != "All");

    return nextCategories.map(cat => {
      if (category.value == "All") {
        return {
          ...cat,
          selected: cat.value == "All"
        };
      } else {
        if (cat.value == "All") {
          return {
            ...cat,
            selected: !flag
          };
        } else {
          return cat;
        }
      }
    });
  };

  let categoriesString = advancedFilters.categories.reduce(
    (accumulator: string, currentValue: dropdownItemSelected) => {
      const selected = advancedFilters.categories.filter(cat => cat.selected);
      if (currentValue.selected) {
        return `${accumulator}${
          currentValue.label != selected[0].label ? ", " : ""
        }${currentValue.label}`;
      } else {
        return accumulator;
      }
    },
    ""
  );

  return (
    <div className="whatson-advanced-filters">
      <div className="whatson-advanced-filters__datepicker">
        <DatePicker
          startDate={startDate === null ? null : startDate.toDate()}
          endDate={endDate === null ? null : endDate.toDate()}
          onDateChange={(startDate, endDate) => {
            if (startDate === null && endDate === null) {
              setDates({
                startDate: moment().startOf("month"),
                endDate: moment().endOf("month")
              });
            } else {
              setDates({
                startDate: startDate ? moment(startDate) : null,
                endDate: endDate ? moment(endDate) : null
              });
            }
          }}
          alwaysOpenOnStart
          externalMonthAndYearState={externalMonthAndYearState}
          customOutsideClickCondition={e => {
            const target = e.target as HTMLElement;
            if (
              (target &&
                target.closest(".dropdown--month-selector") !== null) ||
              (target && target.closest(".dropdown--year-selector") !== null)
            ) {
              return false;
            } else {
              return true;
            }
          }}
        />
      </div>
      {advancedFilters.courtSurfaces.length && advancedFilters.courtDoorness ? (
        <div
          className={`whatson-advanced-filters__surfaces ${
            showSurfaceFilters
              ? ""
              : "whatson-advanced-filters__surfaces--closed"
          }`}
          onClick={() => {
            !showSurfaceFilters ? setShowSurfaceFilters(true) : null;
          }}
        >
          <button
            onClick={() => {
              setShowSurfaceFilters(!showSurfaceFilters);
            }}
            value="Toggle surface"
            className={`whatson-advanced-filters__surface-filter-toggle ${
              showSurfaceFilters
                ? ""
                : "whatson-advanced-filters__surface-filter-toggle--collapsed"
            }`}
          />
          <h3 className="whatson-advanced-filters__surface-title">
            All Surfaces
          </h3>
          <div
            className={`${
              !showSurfaceFilters
                ? "whatson-advanced-filters__surfaces--collapsed"
                : ""
            }`}
          >
            {advancedFilters.courtSurfaces.length ? (
              <div>
                <div
                  className={`whatson-advanced-filters__surface-filters ${
                    advancedFilters.courtSurfaces.length === 1
                      ? "whatson-advanced-filters__surface-filters--disabled"
                      : ""
                  }`}
                >
                  {advancedFilters.courtSurfaces.map(surface => {
                    return (
                      <div
                        key={`${surface.code}${surface.name}`}
                        className={`whatson-advanced-filters__surface-filter ${
                          surface.active
                            ? `whatson-advanced-filters__surface-filter--active`
                            : ""
                        }`}
                      >
                        <label
                          htmlFor={`surface-filter--${surface.code}`}
                          aria-required="false"
                        >
                          {surface.name}
                        </label>
                        <button
                          className={`whatson-advanced-filters__surface-filter-button whatson-advanced-filters__surface-filter-button--${surface.code}`}
                          onClick={() => {
                            const nextSurfaces = changeCourtSufaceState(
                              surface.code,
                              !surface.active,
                              advancedFilters.courtSurfaces
                            );
                            if (!nextSurfaces.filter(op => op.active).length)
                              return;
                            setAdvancedFilters({
                              ...advancedFilters,
                              shouldUpdateData: true,
                              courtSurfaces: nextSurfaces
                            });
                          }}
                          id={`surface-filter--${surface.code}`}
                          title={`Toggle ${surface.name}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
            {advancedFilters.courtDoorness.length ? (
              <>
                <h3>Courts</h3>
                <div className={`whatson-advanced-filters__surface-filters`}>
                  {advancedFilters.courtDoorness.map(door => {
                    return (
                      <div
                        key={door.code}
                        className={`whatson-advanced-filters__court-filter ${
                          door.active
                            ? `whatson-advanced-filters__court-filter--active`
                            : ""
                        }`}
                      >
                        <button
                          className="whatson-advanced-filters__court-filter-button"
                          id="surface-filter--indoor"
                          onClick={() => {
                            const nextDoorness = changeCourtDoornessState(
                              door.code,
                              !door.active,
                              advancedFilters.courtDoorness
                            );
                            if (!nextDoorness.filter(op => op.active).length)
                              return;
                            setAdvancedFilters({
                              ...advancedFilters,
                              shouldUpdateData: true,
                              courtDoorness: nextDoorness
                            });
                          }}
                          title={`Toggle ${
                            door.code === "O" ? "Outdoor" : "Indoor"
                          }`}
                        />
                        <label
                          htmlFor={`surface-filter--${door}`}
                          aria-required="false"
                        >
                          {door.code === "O" ? "Outdoor" : "Indoor"}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="whatson-advanced-filters__surfaces--empty" />
      )}
      {advancedFilters.grandSlams.length ? (
        <div className="whatson-advanced-filters__grandSlams">
          <Dropdown
            options={advancedFilters.grandSlams}
            value={
              advancedFilters.grandSlams.filter(
                (ac: dropdownItem) => ac.selected
              )[0]
            }
            onChange={(e: any) => {
              const grandSlams = [
                ...advancedFilters.grandSlams.map(
                  (grandSlam: dropdownItem) => ({
                    ...grandSlam,
                    selected: grandSlam.value == e.value
                  })
                )
              ];

              setAdvancedFilters({
                ...advancedFilters,
                shouldUpdateData: true,
                grandSlams
              });
            }}
          />
        </div>
      ) : null}
      {advancedFilters.ageCategories.length ? (
        <div className="whatson-advanced-filters__ageCategories ageCategories-dropdown whatson-hero__search-container">
          <Dropdown
            options={advancedFilters.ageCategories}
            value={
              advancedFilters.ageCategories.filter(
                (ac: dropdownItem) => ac.selected
              )[0]
            }
            onChange={(e: any) => {
              const ageCategories = [
                ...advancedFilters.ageCategories.map(
                  (ageCat: dropdownItem) => ({
                    ...ageCat,
                    selected: ageCat.value == e.value
                  })
                )
              ];

              setAdvancedFilters({
                ...advancedFilters,
                shouldUpdateData: true,
                ageCategories
              });
            }}
          />
        </div>
      ) : null}
      {advancedFilters.matchTypes && advancedFilters.matchTypes.length > 1 ? (
        <div className="whatson-advanced-filters__matchtype">
          <Dropdown
            options={advancedFilters.matchTypes}
            value={
              advancedFilters.matchTypes.filter(
                (matchType: dropdownItem) => matchType.selected
              )[0]
            }
            onChange={(matchType: any) => {
              let nextMatchType = advancedFilters.matchTypes.map(type => ({
                ...type,
                selected: matchType.value === type.value
              }));

              setAdvancedFilters({
                ...advancedFilters,
                shouldUpdateData: true,
                matchTypes: nextMatchType
              });
            }}
          />
        </div>
      ) : null}
      {advancedFilters.playerTypes && advancedFilters.playerTypes.length > 1 ? (
        <div className="whatson-advanced-filters__playertype">
          <Dropdown
            options={advancedFilters.playerTypes}
            value={
              advancedFilters.playerTypes.filter(
                (playerType: any) => playerType.selected
              )[0]
            }
            onChange={(playerType: any) => {
              let nextPlayerType = advancedFilters.playerTypes.map(type => ({
                ...type,
                selected: playerType.value === type.value
              }));

              setAdvancedFilters({
                ...advancedFilters,
                shouldUpdateData: true,
                playerTypes: nextPlayerType
              });
            }}
          />
        </div>
      ) : null}
      {advancedFilters.categories.length > 1 ? (
        <MultiSelectDropdown
          className={`whatson-advanced-filters__category`}
          value={categoriesString}
          optionOnClick={(cat: dropdownItemSelected) => {
            const nextCategories = changeCategory(
              cat,
              advancedFilters.categories
            );
            setAdvancedFilters({
              ...advancedFilters,
              shouldUpdateData: true,
              categories: nextCategories
            });
          }}
          options={advancedFilters.categories}
        />
      ) : null}
      {advancedFilters.drawFormat && advancedFilters.drawFormat.length > 0 && (
        <Dropdown
          className={"whatson-advanced-filters__draw-format"}
          options={advancedFilters.drawFormat}
          placeholder="Draw Format"
          value={
            advancedFilters.drawFormat.filter(
              (ac: dropdownItem) => ac.selected
            )[0]
          }
          onChange={(drawFormat: any) => {
            let nextDrawFormat =
              advancedFilters.drawFormat &&
              advancedFilters.drawFormat.map(type => ({
                ...type,
                selected: drawFormat.value === type.value
              }));

            setAdvancedFilters({
              ...advancedFilters,
              shouldUpdateData: true,
              drawFormat: nextDrawFormat
            });
          }}
        />
      )}
    </div>
  );
};

interface WhatsOnFiltersProps {
  loadingError: boolean;
  setLoadingError: (state: boolean) => void;
  searchNationValue: string;
  titleSearchTerm: string;
  errorHandler: Function;
  updateSearchNationValue: Function;
  dateValue: {
    startDate: moment.Moment | null;
    endDate: moment.Moment | null;
  };
  setDates: (dates: Dates) => void;
  setTitleSearchTerm: Function;
  searchStartDate: string | number | (string | number)[] | null | undefined;
  advancedFilters: AdvancedFilters;
  setAdvancedFilters: (filters: AdvancedFilters) => void;
  hostOptions: HostDetails[];
  setHostOptions: (hosts: HostDetails[]) => void;
  tourCode: string;
  results?: boolean;
  tourOptions: dropdownItem[];
  circuitCode: {
    value: string;
    filtersLoaded: boolean;
  };
  updateCircuit: (circuit: { value: string; filtersLoaded: boolean }) => void;
}

const yearSelectionOpts = () => {
  // get current year, go back to 1975
  let year = new Date().getFullYear() + 1;
  let yearSelectionOpts: dropdownItem[] = [];

  while (year > 2000) {
    yearSelectionOpts.push({
      value: year as number,
      label: year.toString()
    });
    year--;
  }

  return yearSelectionOpts;
};

export const WhatsOnFilters = (props: WhatsOnFiltersProps) => {
  const {
    // @ts-ignore
    searchNationValue,
    errorHandler,
    setTitleSearchTerm,
    titleSearchTerm,
    updateSearchNationValue,
    // @ts-ignore
    updateSearchNationOption,
    setDates,
    searchStartDate,
    dateValue,
    advancedFilters,
    setAdvancedFilters,
    hostOptions,
    setHostOptions,
    tourCode,
    results,
    circuitCode,
    updateCircuit,
    loadingError,
    setLoadingError
  } = props;

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [hostNations, setHostNations] = useState<HostDetails[]>([]);
  const [hostRegions, setHostRegions] = useState([]);
  const [focusSearch, setFocusSearch] = React.useState(false);
  const [showHostSearch, setShowHostSearch] = useState(false);

  let dateStartDate =
    dateValue.startDate === null ? null : dateValue.startDate.toDate();
  let dateEndDate =
    dateValue.endDate === null ? null : dateValue.endDate.toDate();

  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();
  const initialMonth = dateStartDate ? dateStartDate.getMonth() + 1 : undefined;
  const initialYear = dateEndDate ? dateEndDate.getFullYear() : undefined;
  const [viewedMonth, setSelectedMonth] = useState<number>(
    initialMonth ? initialMonth : currentMonth
  );
  const [viewedYear, setSelectedYear] = useState(
    initialYear ? initialYear : currentYear
  );
  const externalMonthAndYearState = {
    viewedMonth,
    setViewedMonth: setSelectedMonth,
    viewedYear,
    setViewedYear: setSelectedYear
  };
  let searchGrandSlams = new URLSearchParams(location.search).getAll(
    "grandslams"
  )[0];
  let searchPlayerType = new URLSearchParams(location.search).getAll(
    "playertype"
  )[0];
  let searchAgeCategories = new URLSearchParams(location.search).getAll(
    "agecategories"
  )[0];
  let matchTypeValue = new URLSearchParams(location.search).getAll(
    "matchtype"
  )[0];
  let searchCourtSurfaces = new URLSearchParams(location.search).getAll(
    "courtsurfaces"
  )[0];
  let searchIndoorOutdoor = new URLSearchParams(location.search).getAll(
    "courtdoorness"
  )[0];
  let searchCategories = new URLSearchParams(location.search).getAll(
    "categories"
  )[0];
  let tennisCategories = new URLSearchParams(location.search).getAll(
    "tenniscategories"
  )[0];
  // let singlesDrawFormat = new URLSearchParams(location.search).getAll(
  //   "singlesDrawFormat"
  // )[0];

  useEffect(() => {
    setHostOptions([...hostRegions, ...hostNations]);
  }, [hostNations, hostRegions]);

  const yearOptions: dropdownItem[] = yearSelectionOpts();
  let monthOptions: dropdownItem[] = monthLabelsFull.map((year, index) => {
    return {
      label: year,
      value: index
    };
  });
  monthOptions.unshift({
    label: ALL,
    value: ALL
  });
  let currentMonthIndex = parseInt(moment().format("M")) - 1;
  let selectedMonth = monthOptions[0];
  let searchRef = useRef(null);

  useOutsideAlerter({
    ref: searchRef,
    condition: true,
    callback: () => {
      setShowHostSearch(false);
      updateSearchNationValue("");
    }
  });

  if (
    searchStartDate &&
    searchStartDate.toString().split("-")[1] !== undefined
  ) {
    selectedMonth = monthOptions.filter(month => {
      if (searchStartDate) {
        let selectedMonthIndex =
          parseInt(searchStartDate.toString().split("-")[1]) - 1;
        return month.value === selectedMonthIndex;
      } else {
        return month.value === currentMonthIndex;
      }
    })[0];
  }

  let selectedYear = yearOptions[0];
  if (searchStartDate) {
    selectedYear = yearOptions.filter(year => {
      let selectedYearIndex = parseInt(
        searchStartDate.toString().split("-")[0]
      );
      return year.value === selectedYearIndex;
    })[0];
  }
  // @ts-ignore
  const formatOptionLabel = (props: any) => {
    return (
      <div
        className={`dropdown-item dropdown-item--custom dropdown-item--${props.type}`}
      >
        <span>{props.label}</span>
        <span className={`itf-flags itf-flags--${props.value}`} />
      </div>
    );
  };

  const titleSearch = () => {
    const [inputValue, setInputValue] = useState("");
    let [timeout, updateTimeout] = useState(0);

    const handleOnChange = (value: string) => {
      if (value.length == 0 || value.length > 2) {
        setTitleSearchTerm(value);
      }
    };

    return (
      <div className={`whatson-hero__search-container`}>
        <label
          aria-required="false"
          onClick={() => {
            setFocusSearch(true);
          }}
          className={`whatson-hero__search${
            titleSearchTerm ? " has-value" : ""
          }`}
        >
          <input
            ref={inputRef}
            placeholder="Search Tournament Name"
            onClick={() => {
              setFocusSearch(true);
            }}
            onChange={e => {
              setInputValue(e.target.value);
              useDebounce({
                timeout,
                updateTimeout,
                delay: 600,
                func: handleOnChange,
                params: [e.target.value]
              });
            }}
            value={inputValue}
          />
          {titleSearchTerm ? (
            <a
              className="whatson-hero__search-delete"
              onClick={() => {
                setInputValue("");
                setTitleSearchTerm("");
              }}
            >
              Delete
            </a>
          ) : null}
        </label>
      </div>
    );
  };

  const HostSearchList = (props: {
    results: HostDetails[];
    setHostOptions: (hosts: HostDetails[]) => void;
  }) => {
    const { results, setHostOptions } = props;
    const numberOfZones = results.reduce((acc, item) => {
      if (item.type === "zone") {
        return acc + 1;
      } else {
        return acc;
      }
    }, 0);

    return (
      <div className="whatson-host-list">
        <ul className="whatson-host-list__container">
          {results.map((result, index) => {
            let output = null;
            if (!result.filteredOut) {
              output = (
                <li
                  key={result.code}
                  className={`whatson-host-list__item ${
                    result.active ? "whatson-host-list__item--selected" : ""
                  }
                  ${`whatson-host-list__item--${result.type}`}
                  ${
                    index === numberOfZones - 1
                      ? "whatson-host-list__item--last-nation"
                      : ""
                  }`}
                  onClick={() => {
                    let nextResults = results.map(resultItem => {
                      return {
                        ...resultItem,
                        active:
                          resultItem.code === result.code
                            ? !resultItem.active
                            : resultItem.active
                      } as HostDetails;
                    });
                    if (nextResults.filter(res => res.active).length === 4)
                      return;
                    setHostOptions(nextResults);
                  }}
                >
                  <span>{result.name}</span>
                  <span className="whatson-host-list__meta-container">
                    {result.active ? (
                      <span className="whatson-host-list__selected-tick" />
                    ) : null}
                    <span className={`itf-flags itf-flags--${result.code}`} />
                  </span>
                </li>
              );
            }
            return output;
          })}
        </ul>
      </div>
    );
  };

  const inputRef: any = useRef(null);

  useEffect(() => {
    if (inputRef && inputRef.current !== null && focusSearch) {
      inputRef.current.focus();
      setFocusSearch(false);
    }
  }, [dateValue, focusSearch]);

  useEffect(() => {
    let startDateString = formatDate(dateValue.startDate).raw;
    let endDateString = formatDate(dateValue.endDate).raw;

    const processCalendarData = (res: any) => {
      let nations = results ? res.nations : res.Nations;
      let regions = results ? res.regions : res.Regions;
      let IndoorOutdoor = results ? res.indoorOutdoor : res.IndoorOutdoor;
      let categories = results ? res.categories : res.Categories;
      let surfaces = results ? res.surfaces : res.Surfaces;
      let singlesDrawFormat = results
        ? res.singlesDrawFormat
        : res.SinglesDrawFormat;
      setHostNations(
        nations.map((nation: any) => {
          return {
            name: nation.name,
            code: nation.code,
            type: "nation",
            filteredOut: false,
            active: false
          };
        })
      );

      setHostRegions(
        regions.map((zone: any) => {
          return {
            name: zone.name,
            code: zone.code,
            type: "zone",
            filteredOut: false,
            active: false
          };
        })
      );

      const currentSurfaces = advancedFilters.courtSurfaces;

      surfaces = surfaces.map((surface: any) => {
        const surf = currentSurfaces.filter(surf => surf.name === surface.name);
        const active = surf.length ? surf[0].active : true;
        return {
          name: surface.name,
          code: surface.code,
          active
        };
      });
      const currentDoorness = advancedFilters.courtDoorness;
      const doorness = IndoorOutdoor.filter((door: any) => door).map(
        (door: any) => {
          const dr = currentDoorness.filter(dr => dr.code === door);
          const active = dr.length ? dr[0].active : true;
          return {
            code: door,
            active
          };
        }
      );

      let cats = categories.map((category: any) => {
        return {
          value: category,
          label: category,
          selected: false
        };
      });

      let drawFormat =
        singlesDrawFormat && singlesDrawFormat.length > 0
          ? singlesDrawFormat.map(({ code: value, name: label }: any) => ({
              value,
              label
            }))
          : [];

      if (drawFormat.length > 0) {
        drawFormat.unshift({
          label: "All Draw Formats",
          value: ALL,
          selected: true
        });
      }

      cats.unshift({
        label: "All Categories",
        value: ALL,
        selected: true
      });

      let additionalProps: any = { doorness, categories: cats };

      if (results) {
        let checkFilters = [
          {
            name: "matchTypes",
            param: matchTypeValue,
            option: { label: "Any match type", value: null, selected: false }
          },
          {
            name: "grandSlams",
            param: searchGrandSlams,
            option: {
              label: "All Grand Slams",
              value: "",
              selected: searchGrandSlams == "all" ? true : false
            }
          },
          {
            name: "ageCategories",
            param: searchAgeCategories,
            option: {
              label: "All Groups",
              value: null,
              selected: searchAgeCategories ? false : true
            }
          },
          {
            name: "playerTypes",
            param: searchPlayerType,
            option: {
              label: "Any player type",
              value: null,
              selected: searchPlayerType ? false : true
            }
          },
          { name: "surfaces", param: searchCourtSurfaces, option: null }
        ];

        if (res.tennisCategories) {
          checkFilters.push({
            name: "tennisCategories",
            param: tennisCategories,
            option: null
          });
        }

        checkFilters.forEach(({ name, option, param }: any) => {
          let ifCourts = name === "surfaces";
          let values = res[name].map((val: any) => ({
            [ifCourts ? "name" : "label"]: val.name,
            [ifCourts ? "code" : "value"]: val.code,
            [ifCourts ? "active" : "selected"]:
              ifCourts && param
                ? param.split(",").includes(val.code)
                : param === val.code
          }));

          if (option) {
            values.unshift(option);
          }

          // if tennisCategories has no selected value
          if (
            name == "tennisCategories" &&
            values.every((t: dropdownItem) => !t.selected)
          ) {
            values = values.map((t: dropdownItem, index: number) => ({
              ...t,
              selected: index === 0
            }));
          }

          // make all options active if tour is switched and options can not be carried over from url
          if (ifCourts && values.every((value: any) => !value.active)) {
            values = values.map((value: any) => ({ ...value, active: true }));
          }

          additionalProps[name] = res[name].length ? values : [];
        });

        // Read search param value for IndoorOutdoor
        if (searchIndoorOutdoor) {
          additionalProps.doorness =
            doorness.length > 1
              ? doorness.map((val: any) => ({
                  ...val,
                  active: searchIndoorOutdoor.split(",").includes(val.code)
                }))
              : doorness.map((val: any) => ({ ...val, active: true }));
        }

        // Read search param value for categories
        if (searchCategories) {
          additionalProps.categories = cats.map((val: any) => ({
            ...val,
            selected: searchCategories === val.value
          }));
        }

        // set a default of "ALL" categories if none is selected
        if (
          additionalProps.categories.every((c: dropdownItem) => !c.selected) &&
          circuitCode.value !== ""
        ) {
          additionalProps.categories = cats;
        }

        // set a default of "S" matchType if none is selected
        if (
          additionalProps.matchTypes.every((mt: dropdownItem) => !mt.selected)
        ) {
          additionalProps.matchTypes = additionalProps.matchTypes.map(
            (mt: dropdownItem) => ({ ...mt, selected: mt.value === "S" })
          );
        }
      }

      setAdvancedFilters({
        ...advancedFilters,
        ...additionalProps,
        courtSurfaces: results ? additionalProps.surfaces : surfaces,
        courtDoorness: results ? additionalProps.doorness : doorness,
        categories: results ? additionalProps.categories : cats,
        drawFormat,
        shouldUpdateData: true
      });

      if (!circuitCode.filtersLoaded) {
        updateCircuit({ value: circuitCode.value, filtersLoaded: true });
      }

      if (loadingError) {
        console.log("unset error status");
        setLoadingError(false);
      }
    };

    // update the filters
    if (startDateString && endDateString && !results) {
      getCalendarFilters(tourCode, startDateString, endDateString)
        .then(processCalendarData)
        .catch(() => {
          errorHandler();
          setAdvancedFilters({
            ...advancedFilters,
            shouldUpdateData: true
          });
        });
    } else if (startDateString && endDateString && results) {
      getResultsRoundUpFilters({
        tourCode: circuitCode.value,
        startDate: startDateString,
        endDate: endDateString
      }).then(processCalendarData);
    }
  }, [dateValue, circuitCode.value]);

  useEffect(() => {
    if (!showHostSearch) {
      const refreshedOptions = hostOptions.map(op => ({
        ...op,
        filteredOut: false
      }));
      setHostOptions(refreshedOptions);
    }
  }, [showHostSearch]);

  const tourOptionsDD = () => {
    let options = [
      { label: "All tours", value: "" },
      ...tourOptions.map((op: any) => ({ label: op.name, value: op.code }))
    ];
    return (
      <div className="tourOptions-dropdown whatson-hero__search-container">
        <Dropdown
          options={options}
          value={
            options.filter((op: any) => op.value === circuitCode.value)[0] ||
            props.tourOptions[0]
          }
          onChange={(e: any) => {
            if (!results) {
              location.href = `${location.origin}${e.url}`;
            } else {
              updateCircuit({ value: e.value, filtersLoaded: false });
            }
          }}
        />
      </div>
    );
  };

  const changeLabels = (t: dropdownItem) => ({
    ...t,
    label:
      t.label.indexOf("Men") > -1
        ? "Men's"
        : t.label.indexOf("Women") > -1
        ? "Women's"
        : t.label
  });

  return (
    <div className="whatson-table__filters">
      <div className="page-section">
        <div className="page-section__inner">
          <div className="filters-container">
            <Dropdown
              className={"dropdown--year-selector"}
              options={yearOptions}
              value={selectedYear}
              onChange={year => {
                setSelectedYear(year.value as number);
                if (selectedMonth.value !== ALL) {
                  setDates({
                    startDate: moment()
                      .year(year.value as number)
                      .month(selectedMonth.value)
                      .startOf("month"),
                    endDate: moment()
                      .year(year.value as number)
                      .month(selectedMonth.value)
                      .endOf("month")
                  });
                } else {
                  setDates({
                    startDate: moment()
                      .year(year.value as number)
                      .startOf("year"),
                    endDate: moment()
                      .year(year.value as number)
                      .endOf("year")
                  });
                }
              }}
            />
            <Dropdown
              className={"dropdown--month-selector"}
              options={monthOptions}
              value={selectedMonth}
              onChange={month => {
                if (month.value !== ALL) {
                  setSelectedMonth((month.value as number) + 1);
                  setDates({
                    startDate: moment()
                      .year(selectedYear.value as number)
                      .month(month.value)
                      .startOf("month"),
                    endDate: moment()
                      .year(selectedYear.value as number)
                      .month(month.value)
                      .endOf("month")
                  });
                } else {
                  // setSelectedMonth(ALL);
                  setDates({
                    startDate: moment()
                      .year(selectedYear.value as number)
                      .startOf("year"),
                    endDate: moment()
                      .year(selectedYear.value as number)
                      .endOf("year")
                  });
                }
              }}
            />
            <div className="whatson-table__mobile-date-picker whatson-advanced-filters__datepicker">
              <DatePicker
                startDate={
                  dateValue.startDate === null
                    ? null
                    : dateValue.startDate.toDate()
                }
                endDate={
                  dateValue.endDate === null ? null : dateValue.endDate.toDate()
                }
                onDateChange={(startDate, endDate) => {
                  if (startDate === null && endDate === null) {
                    setDates({
                      startDate: moment().startOf("month"),
                      endDate: moment().endOf("month")
                    });
                  } else {
                    setDates({
                      startDate: startDate ? moment(startDate) : null,
                      endDate: endDate ? moment(endDate) : null
                    });
                  }
                }}
              />
            </div>
            {circuitCode.value === "SL" &&
            (advancedFilters.tennisCategories &&
              advancedFilters.tennisCategories.length) ? (
              <div className="whatson-table__host-search-container">
                <Dropdown
                  options={
                    advancedFilters.tennisCategories
                      ? advancedFilters.tennisCategories.map(changeLabels)
                      : []
                  }
                  value={
                    advancedFilters.tennisCategories
                      ? advancedFilters.tennisCategories
                          .map(changeLabels)
                          .filter((t: dropdownItem) => t.selected)[0]
                      : null
                  }
                  onChange={target => {
                    const tennisCategories = advancedFilters.tennisCategories
                      ? advancedFilters.tennisCategories.map(
                          (t: dropdownItem) => ({
                            ...t,
                            selected: t.value === target.value
                          })
                        )
                      : [];

                    setAdvancedFilters({
                      ...advancedFilters,
                      tennisCategories
                    });
                  }}
                />
              </div>
            ) : (
              <div className="whatson-table__host-search-container">
                <label
                  ref={searchRef}
                  aria-required="false"
                  className={`whatson-table__search
              ${searchNationValue ? " has-value" : ""}
              ${showHostSearch ? " whatson-table__search--open" : ""}
            `}
                >
                  <input
                    value={searchNationValue}
                    placeholder="Search host nation or region"
                    onClick={() => {
                      if (!showHostSearch) {
                        setShowHostSearch(true);
                      }
                    }}
                    onFocus={() => {
                      updateSearchNationValue("");
                      setShowHostSearch(true);
                    }}
                    onChange={(e: React.ChangeEvent) => {
                      const target: any = e.target;
                      updateSearchNationValue(target.value);
                      if (target.value.length > 0) {
                        setShowHostSearch(true);
                      } else {
                        setShowHostSearch(false);
                      }
                      let nextResults = hostOptions
                        .map(option => {
                          return {
                            ...option,
                            filteredOut: option.name
                              .toLowerCase()
                              .includes(target.value.toLowerCase())
                              ? false
                              : true
                          } as HostDetails;
                        })
                        .filter(res => res.type != "no-results");
                      setHostOptions(nextResults);

                      if (!nextResults.filter(res => !res.filteredOut).length) {
                        setHostOptions([
                          ...hostOptions
                            .filter(res => res.type !== "no-results")
                            .map(res => {
                              res.filteredOut = true;
                              return res;
                            }),
                          {
                            name: `There are no results for your search "${target.value}"`,
                            code: "",
                            type: "no-results",
                            filteredOut: false,
                            active: false
                          }
                        ]);
                      }

                      // filter the hostOptions here based on the text
                    }}
                  />
                  <button
                    onClick={() => {
                      setShowHostSearch(!showHostSearch);
                    }}
                    value="Toggle Nation Search"
                    className={`whatson-table__search-toggle`}
                  />
                  {showHostSearch && searchNationValue ? (
                    <a
                      className="whatson-hero__search-delete"
                      onClick={() => {
                        setShowHostSearch(false);
                        updateSearchNationValue("");
                        const refreshedOptions = hostOptions.map(op => ({
                          ...op,
                          filteredOut: false
                        }));
                        setHostOptions(refreshedOptions);
                      }}
                    >
                      Delete
                    </a>
                  ) : null}
                  {showHostSearch ? (
                    <div className="whatson-table__search-results">
                      <HostSearchList
                        results={hostOptions}
                        setHostOptions={setHostOptions}
                      />
                    </div>
                  ) : null}
                </label>
              </div>
            )}

            {results ? tourOptionsDD() : titleSearch()}

            <div className="whatson-table__advanced-toggle-container">
              {!showAdvancedFilters ? (
                <button
                  className="btn btn--primary whatson-table__advanced-toggle"
                  onClick={() => {
                    setShowAdvancedFilters(true);
                  }}
                >
                  More filters
                </button>
              ) : (
                <button
                  className="btn btn--primary whatson-table__advanced-toggle"
                  onClick={() => {
                    setShowAdvancedFilters(false);
                  }}
                >
                  Less filters
                </button>
              )}
            </div>
          </div>

          <div>
            {showAdvancedFilters ? (
              <WhatsOnAdvancedFilters
                startDate={dateValue.startDate}
                endDate={dateValue.endDate}
                setDates={setDates}
                advancedFilters={advancedFilters}
                setAdvancedFilters={setAdvancedFilters}
                externalMonthAndYearState={externalMonthAndYearState}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const WhatsOnTable = ({
  getData = new Promise(resolve => resolve()),
  // tourOptions = [],
  tourCode,
  tournamentSearchResults,
  dateSelection,
  reset,
  titleSearchTerm = "",
  takeValue,
  setTake,
  dateValue,
  setDates,
  searchNationValue,
  updateSearchNationValue,
  setTitleSearchTerm,
  isLoading,
  setIsLoading,
  loadingError,
  searchStartDate,
  areNoResults,
  isProgressiveLoading,
  setIsProgressiveLoading,
  advancedFilters,
  setAdvancedFilters,
  hostOptions,
  setHostOptions,
  setSortCriteria,
  sortCriteria,
  results,
  circuitCode,
  updateCircuit,
  setLoadingError
}: WhatsOnTableProps) => {
  let [tableResults, setTableResults] = useState<any>({
    items: []
  });
  let [page, setPage] = useState(1);
  let remaining = tournamentSearchResults.totalItems - takeValue * page;
  let [resultRemaining, setResultsRemaining] = useState<number>(
    tournamentSearchResults.totalItems - takeValue * page
  );
  let [pagerLoadingError, setPagerLoadingError] = useState<boolean>(false);

  const errorHandler = (err: any) => {
    setIsLoading(false);
    setPagerLoadingError(true);
    console.error("Couldn't complete fetch request", err);
  };

  const updateResultsRemaining = (res: any) => {
    const remaining = res.totalItems - res.items.length;
    setResultsRemaining(remaining);
  };

  const pagerFlag =
    remaining > takeValue || (remaining > 0 && remaining < takeValue);

  let pagerOptions = perPage
    .map((op: { label: string; value: number | string; status?: boolean }) => {
      return {
        label: op.label,
        value: op.value,
        status:
          tableResults.totalItems /
            (typeof op.value == "number" ? op.value : 0) >
          1
      };
    })
    .filter(op => op.status);

  if (circuitCode.value) {
    pagerOptions.push({
      label: "ALL",
      value: tableResults.totalItems,
      status: true
    });
  }

  const handlePager = ({ value }: any) => {
    setIsLoading(true);
    setPage(1);

    if (pagerLoadingError) setPagerLoadingError(false);

    getData({
      ...dateSelection,
      tourCode,
      q: titleSearchTerm,
      skip: 0,
      take: value
    })
      .then((res: any) => {
        updateResultsRemaining({ ...tableResults, ...res });
        setTake(value);
        setTableResults({ ...tableResults, ...res });
        setIsLoading(false);
      })
      .catch(errorHandler);
  };

  const handleProgressiveLoad = () => {
    setIsProgressiveLoading(true);
    const skip = page * takeValue;
    const newTake =
      resultRemaining > 0 && resultRemaining < takeValue
        ? resultRemaining
        : takeValue;

    if (pagerLoadingError) setPagerLoadingError(false);

    getData({
      q: titleSearchTerm,
      skip,
      take: newTake,
      tourCode,
      startDate: formatDate(dateValue.startDate).raw,
      endDate: formatDate(dateValue.endDate).raw
    })
      .then((res: any) => {
        const items = [...tableResults.items, ...res.items];
        const remaining = res.totalItems - items.length;

        setPage(++page);
        setTableResults({ ...res, items });
        setResultsRemaining(remaining);
        setIsProgressiveLoading(false);
      })
      .catch(errorHandler);
  };

  useEffect(() => {
    if (reset) {
      const remaining =
        tournamentSearchResults.totalItems -
        tournamentSearchResults.items.length;
      setPage(1);
      setResultsRemaining(remaining);
      setTableResults(tournamentSearchResults);
    }
  });

  const circuit = {
    circuitCode,
    updateCircuit
  };

  const matchType = advancedFilters.matchTypes.filter(
    (mt: dropdownItem) => mt.selected
  );
  const currentMatchTypeCode = matchType.length ? matchType[0].value : "S";

  let pagerValue = takeValue === tableResults.totalItems ? "ALL" : takeValue;

  return (
    <div
      className={`whatson-table${!tableResults.totalItems ? " noResults" : ""}
        whatson-table--${
          !circuitCode.filtersLoaded ? "hide" : circuitCode.value
        }${results ? " results" : ""}
      `}
    >
      <WhatsOnFilters
        loadingError={loadingError}
        setLoadingError={setLoadingError}
        errorHandler={errorHandler}
        searchNationValue={searchNationValue}
        titleSearchTerm={titleSearchTerm}
        updateSearchNationValue={updateSearchNationValue}
        dateValue={dateValue}
        setDates={setDates}
        setTitleSearchTerm={setTitleSearchTerm}
        searchStartDate={searchStartDate}
        advancedFilters={advancedFilters}
        setAdvancedFilters={setAdvancedFilters}
        hostOptions={hostOptions}
        setHostOptions={setHostOptions}
        tourCode={tourCode}
        results={results}
        tourOptions={tourOptions}
        {...circuit}
      />
      <WhatsOnStateToggleSection
        titleSearchTerm={titleSearchTerm}
        dateValue={dateValue}
        resetHost={() => {
          updateSearchNationValue("");
        }}
        resetDates={() =>
          setDates({
            startDate: moment().startOf("month"),
            endDate: moment().endOf("month")
          })
        }
        resetTitle={() => {
          setTitleSearchTerm("");
        }}
        advancedFilters={advancedFilters}
        setAdvancedFilters={setAdvancedFilters}
        hostOptions={hostOptions}
        setHostOptions={setHostOptions}
      />
      <section className={`page${reset ? "-loading" : "-section"}`}>
        <div className="page-section__inner">
          {loadingError ? (
            <span className="error-text">
              {tournamentCalendar.catchMessage}
            </span>
          ) : null}

          {areNoResults && !loadingError ? (
            <h2>{tournamentCalendar.noResultsMessage}</h2>
          ) : !isLoading && !loadingError ? (
            <>
              {results ? (
                <Results
                  {...tableResults}
                  tourCode={circuitCode.value}
                  matchTypeCode={currentMatchTypeCode}
                  setSortCriteria={setSortCriteria}
                  sortCriteria={sortCriteria}
                />
              ) : (
                <Table
                  {...tableResults}
                  tourCode={tourCode}
                  setSortCriteria={setSortCriteria}
                  sortCriteria={sortCriteria}
                />
              )}
            </>
          ) : null}
          {/* {pagerLoadingError ? (
            <div className="page-section page-section--align-center page-section--no-padding">
              <p className="error-text">{tournamentCalendar.catchMessage}</p>
            </div>
          ) : null} */}
          <div
            className={`pager-wrapper${
              !pagerFlag ? "--no-btn" : ""
            } flex-container${
              isLoading || isProgressiveLoading ? " page-loading" : ""
            }`}
          >
            {pagerFlag ? (
              <>
                <button onClick={handleProgressiveLoad} className="btn">
                  Show{" "}
                  {resultRemaining > 0 && resultRemaining < takeValue
                    ? resultRemaining
                    : takeValue}{" "}
                  more
                </button>
              </>
            ) : null}
            {!areNoResults && !loadingError ? (
              <div className="pager">
                <span className="pager__tablet-text">Showing</span>
                <Dropdown
                  placeholder={takeValue}
                  options={pagerOptions}
                  value={
                    pagerOptions.filter(
                      (op: dropdownItem) => op.value == pagerValue
                    )[0]
                  }
                  onChange={handlePager}
                />
                {pagerFlag ? <span>items per page</span> : null}
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
};

export default (props: WhatsOnTableProps) =>
  useLazyLoad(<WhatsOnTable {...props} />);
